import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  deletePlatformNotification,
  fetchPlatformNotifications,
  markAllPlatformNotificationsRead,
  markPlatformNotificationRead,
} from '../../../api/platformNotifications.api';
import type { PlatformNotificationItem } from '../../../types/platformNotification.types';

const PER_PAGE = 15;

const categoryLabels: Record<string, string> = {
  system: 'نظام',
  subscription: 'اشتراكات',
  payment: 'مدفوعات',
  support: 'دعم',
};

const priorityBadge: Record<string, string> = {
  high: 'bg-rose-50 text-rose-600 ring-rose-100',
  normal: 'bg-teal-50 text-teal-600 ring-teal-100',
  low: 'bg-gray-50 text-gray-500 ring-gray-100',
};

export default function SystemNotificationsInbox() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<PlatformNotificationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');

  const load = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const r = await fetchPlatformNotifications(page, PER_PAGE, {
        search: search.trim() || undefined,
        category: category || undefined,
      });
      setLoading(false);
      if (r.ok === false) {
        if (r.unauthorized) {
          navigate('/admin/login', { replace: true });
          return;
        }
        setError(r.message);
        setRows([]);
        return;
      }
      setRows(r.list.data);
      setLastPage(Math.max(1, r.list.last_page));
      setTotal(r.list.total);
      setUnread(r.list.stats?.unread ?? 0);
    } catch (err) {
      setLoading(false);
      setError(err instanceof Error ? err.message : 'فشل تحميل الإشعارات');
      setRows([]);
    }
  }, [page, search, category, navigate]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleOpen = async (item: PlatformNotificationItem) => {
    if (!item.read_at) {
      await markPlatformNotificationRead(item.id);
      setRows((prev) =>
        prev.map((r) => (r.id === item.id ? { ...r, read_at: new Date().toISOString() } : r)),
      );
      setUnread((n) => Math.max(0, n - 1));
    }
    if (item.action_url) {
      navigate(item.action_url);
    }
  };

  const handleMarkAll = async () => {
    await markAllPlatformNotificationsRead();
    await load();
  };

  const handleDelete = async (id: number) => {
    const r = await deletePlatformNotification(id);
    if (r.ok) await load();
  };

  return (
    <div className="bg-white rounded-xl ring-1 ring-gray-100 overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-gray-100">
        <div>
          <h3 className="text-base font-bold text-gray-900">إشعارات النظام</h3>
          <p className="text-xs text-gray-400 mt-0.5">
            {unread > 0 ? `${unread} غير مقروء من ${total}` : `${total} إشعار`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <i className="ri-search-line absolute start-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setPage(1);
                setSearch(e.target.value);
              }}
              placeholder="بحث..."
              className="ps-9 pe-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 w-44"
            />
          </div>
          <select
            value={category}
            onChange={(e) => {
              setPage(1);
              setCategory(e.target.value);
            }}
            className="py-2 px-3 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30"
          >
            <option value="">كل التصنيفات</option>
            <option value="system">نظام</option>
            <option value="subscription">اشتراكات</option>
            <option value="payment">مدفوعات</option>
          </select>
          {unread > 0 ? (
            <button
              type="button"
              onClick={() => void handleMarkAll()}
              className="px-3 py-2 text-xs font-semibold text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg cursor-pointer"
            >
              <i className="ri-check-double-line me-1" />
              قراءة الكل
            </button>
          ) : null}
        </div>
      </div>

      {error ? (
        <p className="text-sm text-rose-600 px-5 py-4">{error}</p>
      ) : loading ? (
        <p className="text-sm text-gray-400 text-center py-12">جاري التحميل...</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-12">لا توجد إشعارات</p>
      ) : (
        <div className="divide-y divide-gray-50">
          {rows.map((item) => (
            <div
              key={item.id}
              className={`flex items-start gap-4 px-5 py-4 hover:bg-gray-50/80 transition-colors ${
                !item.read_at ? 'bg-sky-50/40' : ''
              }`}
            >
              <div
                className={`w-10 h-10 flex items-center justify-center rounded-xl shrink-0 ${
                  !item.read_at ? 'bg-teal-50 text-teal-600' : 'bg-gray-50 text-gray-400'
                }`}
              >
                <i className="ri-notification-3-line text-lg" />
              </div>
              <button
                type="button"
                onClick={() => void handleOpen(item)}
                className="flex-1 text-start min-w-0 cursor-pointer"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900">{item.title}</span>
                  {!item.read_at ? (
                    <span className="text-[10px] font-bold text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded">
                      جديد
                    </span>
                  ) : null}
                  <span
                    className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ring-1 ${
                      priorityBadge[item.priority] ?? priorityBadge.normal
                    }`}
                  >
                    {item.priority === 'high' ? 'عاجل' : item.priority === 'low' ? 'منخفض' : 'عادي'}
                  </span>
                  <span className="text-[10px] text-gray-400">
                    {categoryLabels[item.category] ?? item.category}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-1 line-clamp-2">{item.message}</p>
                <p className="text-[11px] text-gray-400 mt-1">
                  {item.created_at.slice(0, 16).replace('T', ' ')}
                </p>
              </button>
              <button
                type="button"
                onClick={() => void handleDelete(item.id)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-rose-600 hover:bg-rose-50 cursor-pointer shrink-0"
                title="حذف"
              >
                <i className="ri-delete-bin-line text-sm" />
              </button>
            </div>
          ))}
        </div>
      )}

      {lastPage > 1 ? (
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50/50">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 disabled:opacity-40 cursor-pointer"
          >
            السابق
          </button>
          <span className="text-xs text-gray-500">
            صفحة {page} من {lastPage}
          </span>
          <button
            type="button"
            disabled={page >= lastPage}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 disabled:opacity-40 cursor-pointer"
          >
            التالي
          </button>
        </div>
      ) : null}
    </div>
  );
}
